import { ApiClientImplementation } from 'infra/api-client/api-client'

import { PrepareApiRequestDecorator } from 'main/decorators/prepare-api-request-decorator/prepare-api-request-decorator'
import { makeLocalStorageAdapter } from 'main/factories/cache/local-storage-adapter-factory'
import { ApiRepository } from 'repository/api-repository'
import { SurgicalOrderRepository } from 'repository/repositories/surgical-order/surgical-order-repository'
import { RemoteLoadSurgicalOrderDocument } from 'service/usecases/load-surgical-order-document/load-upload-surgical-order-document'
import SurgicalOrderService from 'presentation/contexts/services/surgical-order-service'
import { RemoteUploadSurgicalOrderDocument } from 'service/usecases/upload-surgical-order-document/remote-upload-surgical-order-document'
import { RemoteLoadSurgicalOrder } from 'service/usecases/load-surgical-order/remote-load-surgical-order'
import { RemoteLoadCid } from 'service/usecases/load-cid/remote-load-cid'
import { RemoteConcludeSurgicalOrder } from 'service/usecases/conclude-surgical-order/remote-conclude-surgical-order'

import { RemoteDeleteSurgicalOrderSolicitation } from 'service/usecases/delete-surgical-order-solicitation/remote-delete-surgical-order-solicitation'
import { RemoteUploadSurgicalOrderDocumentsSameType } from 'service/usecases/upload-surgical-order-documents-same-type/remote-upload-surgical-order-documents-same-type'
import { RemoteLoadFavoriteSurgicalOrders } from 'service/usecases/load-favorite-surgical-orders/remote-load-favorite-surgical-orders'
import { RemoteDeleteFavoritedSurgicalOrder } from 'service/usecases/delete-favorited-surgical-order/remote-delete-favorited-surgical-order'
import { RemoteCheckinSurgicalOrder } from 'service/usecases/checkin-surgical-order/remote-checkin-surgical-order'
import { RemoteLoadFavoriteDoctorSurgicalOrders } from 'service/usecases/load-favorite-doctor-orders/remote-load-favorite-doctor-orders'
import { RemoteUploadToMergeDocument } from 'service/usecases/upload-to-merge-document/remote-upload-to-merge-document'
import { RemoteConfirmMergeDocuments } from 'service/usecases/confirm-merge-documents/remote-confirm-merge-documents'
import { RemoteDeleteUploadedDocuments } from 'service/usecases/delete-uploaded-document-surgical-order/remote-delete-uploaded-document-surgical-order'
import { RemoteLoadGuidesByType } from 'service/usecases/load-guides-by-type/remote-load-guides-by-type'
import { RemoteCreateSurgicalOrderStatus } from 'service/usecases/create-surgical-order-status/remote-create-surgical-order-status'
import { RemoteLoadReasonsCancellation } from 'service/usecases/load-reasons-cancellation/load-reasons-cancellation'
import { RemoteCancelSurgicalOrder } from 'service/usecases/cancel-surgical-order/cancel-surgical-order'
import { RemoteCheckGuidesGenerated } from 'service/usecases/check-guides-generated/remote-check-guides-generated'
import { RemoteGenerateGuides } from 'service/usecases/regenerate-guides/remote-regenerate-guides'
import { RemoteSearchSurgicalOrdersNew } from 'service/usecases/search-surgical-orders/remote-search-surgical-orders-new'
import { RemoteGetSurgicalOrderCounter } from 'service/usecases/get-surgical-order-counter/get-surgical-order-counter'
import { RemoteGetCrmoStatusCounters } from 'service/usecases/get-crmo-status-counters/remote-get-crmo-status-counters'
import { RemoteGetCrmoPendencyCounters } from 'service/usecases/get-crmo-pendency-counters/remote-get-crmo-pendency-counters'
import { RemoteGetSurgicalOrderSchedulingCounter } from 'service/usecases/get-surgical-order-scheduling-counter/get-surgical-order-scheduling-counter'
import { RemoteFinishActiveSearchers } from 'service/usecases/finish-active-searchers/finish-arcitve-searchers'
import { RemoteGetFinalizeSurgicalOrdersHistory } from 'service/usecases/get-finalize-surgical-orders-history/remote-get-finalize-surgical-orders-history'
import { RemoteEditFinalizeSurgicalOrder } from 'service/usecases/edit-finalize-surgical-order/edit-finalize-surgical-order'
import { RemoteCreateOrUpdateBudget } from 'service/usecases/create-or-update-budget/create-or-update-budget'
import { RemoteSearchSurgicalOrdersSchedules } from 'service/usecases/search-surgical-orders-schedules/remote-search-surgical-orders-schedules'
import { RemoteUpdateSurgicalOrderClinicalRecord } from 'service/usecases/update-surgical-order-clinical-record/remote-update-surgical-order-clinical-record'
import { RemoteGetPresignedUrl } from 'service/usecases/get-presigned-url/remote-get-presigned-url'
import { RemoteUploadFileToS3 } from 'service/usecases/upload-file-to-s3/remote-upload-file-to-s3'
import { S3UploadClientImplementation } from 'infra/s3-upload/s3-upload-client'
import { RemoteProcessDocumentToMerge } from 'service/usecases/process-document-to-merge/remote-process-document-to-merge'
import { RemoteGetPresignedUrlCrmo } from 'service/usecases/get-presigned-url-crmo/remote-get-presigned-url-crmo'
import { RemoteProcessDocumentCrmo } from 'service/usecases/process-document-crmo/remote-process-document-crmo'
import { RemoteMergeDocumentsCrmo } from 'service/usecases/merge-documents-crmo/remote-merge-documents-crmo'
import { PrepareApiRequestSurgicalPresignedUrlDecorator } from 'main/decorators/prepare-api-request-surgical-presigned-url-decorator/prepare-api-request-surgical-presigned-url-decorator'
import { PresignedUrlRepository } from 'repository/repositories/surgical-order/presigned-url-repository'

export default function makeSurgicalOrderService() {
  const apiRepository = new PrepareApiRequestDecorator(
    makeLocalStorageAdapter(),
    new ApiRepository(new ApiClientImplementation())
  )

  const surgicalOrderRepository = new SurgicalOrderRepository(apiRepository)

  const loadSurgicalOrderDocument = new RemoteLoadSurgicalOrderDocument(
    surgicalOrderRepository
  )
  const uploadSurgicalOrderDocument = new RemoteUploadSurgicalOrderDocument(
    surgicalOrderRepository
  )
  const loadSurgicalOrder = new RemoteLoadSurgicalOrder(surgicalOrderRepository)
  const loadCids = new RemoteLoadCid(surgicalOrderRepository)
  const concludeSurgicalOrder = new RemoteConcludeSurgicalOrder(
    surgicalOrderRepository
  )
  const deleteSurgicalOrderSolicitation =
    new RemoteDeleteSurgicalOrderSolicitation(surgicalOrderRepository)
  const checkin = new RemoteCheckinSurgicalOrder(surgicalOrderRepository)

  const uploadSurgicalOrderDocumentsSameType =
    new RemoteUploadSurgicalOrderDocumentsSameType(surgicalOrderRepository)

  const loadFavoriteSurgicalOrders = new RemoteLoadFavoriteSurgicalOrders(
    surgicalOrderRepository
  )

  const loadFavoriteDoctorSurgicalOrders =
    new RemoteLoadFavoriteDoctorSurgicalOrders(surgicalOrderRepository)

  const deleteFavoritedSurgicalOrder = new RemoteDeleteFavoritedSurgicalOrder(
    surgicalOrderRepository
  )

  const uploadToMergeDocument = new RemoteUploadToMergeDocument(
    surgicalOrderRepository
  )

  const confirmMergeDocuments = new RemoteConfirmMergeDocuments(
    surgicalOrderRepository
  )

  const deleteUploadedDocuments = new RemoteDeleteUploadedDocuments(
    surgicalOrderRepository
  )

  const loadGuidesByType = new RemoteLoadGuidesByType(surgicalOrderRepository)

  const createSurgicalOrderStatus = new RemoteCreateSurgicalOrderStatus(
    surgicalOrderRepository
  )

  const loadReasonsCancellation = new RemoteLoadReasonsCancellation(
    surgicalOrderRepository
  )

  const cancelSurgicalOrder = new RemoteCancelSurgicalOrder(
    surgicalOrderRepository
  )

  const checkGuidesGenerated = new RemoteCheckGuidesGenerated(
    surgicalOrderRepository
  )

  const regenerateGuides = new RemoteGenerateGuides(surgicalOrderRepository)

  const searchSurgicalOrdersNew = new RemoteSearchSurgicalOrdersNew(
    surgicalOrderRepository
  )

  const getSurgicalOrderCounter = new RemoteGetSurgicalOrderCounter(
    surgicalOrderRepository
  )

  const getSurgicalOrderSchedulingCounter =
    new RemoteGetSurgicalOrderSchedulingCounter(surgicalOrderRepository)

  const getCrmoStatusCounters = new RemoteGetCrmoStatusCounters(
    surgicalOrderRepository
  )

  const getCrmoPendencyCounters = new RemoteGetCrmoPendencyCounters(
    surgicalOrderRepository
  )

  const finishActiveSearchers = new RemoteFinishActiveSearchers(
    surgicalOrderRepository
  )

  const getFinalizeSurgicalOrdersHistory =
    new RemoteGetFinalizeSurgicalOrdersHistory(surgicalOrderRepository)

  const editFinalizeSurgicalOrder = new RemoteEditFinalizeSurgicalOrder(
    surgicalOrderRepository
  )

  const createOrUpdateBudget = new RemoteCreateOrUpdateBudget(
    surgicalOrderRepository
  )

  const searchSurgicalOrdersSchedules = new RemoteSearchSurgicalOrdersSchedules(
    surgicalOrderRepository
  )

  const updateSurgicalOrderClinicalRecord =
    new RemoteUpdateSurgicalOrderClinicalRecord(surgicalOrderRepository)

  const presignedUrlRepository = new PresignedUrlRepository(
    new PrepareApiRequestSurgicalPresignedUrlDecorator(
      makeLocalStorageAdapter(),
      new ApiRepository(new ApiClientImplementation())
    )
  )

  const getPresignedUrl = new RemoteGetPresignedUrl(presignedUrlRepository)

  const uploadFileToS3 = new RemoteUploadFileToS3(
    new S3UploadClientImplementation()
  )

  const processDocumentToMerge = new RemoteProcessDocumentToMerge(
    surgicalOrderRepository
  )

  const getPresignedUrlCrmo = new RemoteGetPresignedUrlCrmo(
    presignedUrlRepository
  )

  const processDocumentCrmo = new RemoteProcessDocumentCrmo(
    surgicalOrderRepository
  )

  const mergeDocumentsCrmo = new RemoteMergeDocumentsCrmo(
    surgicalOrderRepository
  )

  return new SurgicalOrderService({
    loadSurgicalOrderDocument,
    uploadSurgicalOrderDocument,
    loadSurgicalOrder,
    loadCids,
    concludeSurgicalOrder,
    deleteSurgicalOrderSolicitation,
    uploadSurgicalOrderDocumentsSameType,
    loadFavoriteSurgicalOrders,
    loadFavoriteDoctorSurgicalOrders,
    deleteFavoritedSurgicalOrder,
    checkin,
    uploadToMergeDocument,
    confirmMergeDocuments,
    deleteUploadedDocuments,
    loadGuidesByType,
    createSurgicalOrderStatus,
    loadReasonsCancellation,
    cancelSurgicalOrder,
    checkGuidesGenerated,
    regenerateGuides,
    searchSurgicalOrdersNew,
    getSurgicalOrderCounter,
    getSurgicalOrderSchedulingCounter,
    getCrmoStatusCounters,
    getCrmoPendencyCounters,
    finishActiveSearchers,
    getFinalizeSurgicalOrdersHistory,
    editFinalizeSurgicalOrder,
    createOrUpdateBudget,
    searchSurgicalOrdersSchedules,
    updateSurgicalOrderClinicalRecord,
    getPresignedUrl,
    uploadFileToS3,
    processDocumentToMerge,
    getPresignedUrlCrmo,
    processDocumentCrmo,
    mergeDocumentsCrmo
  })
}
